'use client'

import React from 'react'
import useSWR, { useSWRConfig } from 'swr'
import { BadgeCheck, Loader2, MoreHorizontal } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { API_URL } from '@/utils/constants'
import Wallet from '@/utils/wallet'
import { cn } from '@/lib/utils'
import { isVerified } from '../lib/amm'
import { useAlkanes } from '../lib/store'
import { strictFetcher } from '../lib/strict-fetcher'
import { signMessageWithWallet } from '../lib/wallet-signature'

function verificationMessage(assetId: string, timestampMs: number): string {
	return `Ordinals Wallet Alkanes\nVerify asset: ${assetId}\nTimestamp: ${timestampMs}`
}

export default function AssetAdminMenu({ assetId }: { assetId: string }) {
	const { mutate } = useSWRConfig()
	const loggedIn = useAlkanes((state) => state.loggedIn)
	const watchAddress = useAlkanes((state) => state.watchAddress)
	const verifiedIds = useAlkanes((state) => state.verifiedIds)
	const address = loggedIn && !watchAddress ? Wallet.getTaprootAddress() : undefined
	const { data } = useSWR<{ admin?: boolean }>(
		address ? `${API_URL}/alkane/verified-assets/admin/${encodeURIComponent(address)}` : null,
		strictFetcher,
		{ shouldRetryOnError: false, revalidateOnFocus: false }
	)
	const [state, setState] = React.useState<'idle' | 'signing' | 'success' | 'error'>('idle')
	const [error, setError] = React.useState('')
	const alreadyVerified = isVerified(assetId, verifiedIds)

	if (!data?.admin || !address) {
		return null
	}

	const verify = async () => {
		if (state === 'signing' || alreadyVerified) {
			return
		}
		setState('signing')
		setError('')
		try {
			const timestampMs = Date.now()
			const signature = await signMessageWithWallet(verificationMessage(assetId, timestampMs))
			const response = await fetch(`${API_URL}/alkane/verified-assets`, {
				method: 'POST',
				headers: { 'content-type': 'application/json' },
				body: JSON.stringify({ address, assetId, timestampMs, signature })
			})
			if (!response.ok) {
				// The API has two error shapes: {error: "why"} and {error: true, message: "why"}.
				const payload = (await response.json().catch(() => null)) as {
					error?: string | boolean
					message?: string
				} | null
				const reason = (typeof payload?.error === 'string' && payload.error) || payload?.message
				throw new Error(reason || 'Verification failed')
			}
			await mutate(`${API_URL}/alkane/verified-assets`)
			setState('success')
			window.setTimeout(() => setState('idle'), 5_000)
		} catch (cause) {
			setError(cause instanceof Error ? cause.message : 'Verification failed')
			setState('error')
			window.setTimeout(() => setState('idle'), 5_000)
		}
	}

	return (
		<Popover>
			<PopoverTrigger asChild>
				<button
					type="button"
					aria-label="Token admin actions"
					data-testid="alkanes-asset-admin-menu"
					className="grid size-8 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
				>
					<MoreHorizontal className="size-4" aria-hidden="true" />
				</button>
			</PopoverTrigger>
			<PopoverContent align="start" className="alkanes-ink w-60 p-1.5">
				<button
					type="button"
					onClick={verify}
					disabled={alreadyVerified || state === 'signing' || state === 'success'}
					className={cn(
						'flex min-h-10 w-full items-center gap-2 rounded-lg px-3 text-left text-sm font-medium transition-colors',
						state === 'success' && 'bg-success/10 text-success-ink',
						state === 'error' && 'bg-destructive/10 text-destructive-ink',
						state === 'idle' && !alreadyVerified && 'hover:bg-secondary',
						(alreadyVerified || state === 'signing') && 'text-muted-foreground'
					)}
				>
					{state === 'signing' ? (
						<Loader2 className="size-4 animate-spin" aria-hidden="true" />
					) : (
						<BadgeCheck className="size-4" aria-hidden="true" />
					)}
					<span className="min-w-0 flex-1 truncate">
						{alreadyVerified || state === 'success'
							? 'Verified'
							: state === 'signing'
							? 'Sign in wallet…'
							: state === 'error'
							? error
							: 'Verify token'}
					</span>
				</button>
			</PopoverContent>
		</Popover>
	)
}
